import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { EventSheet, useFamilyPeople, type EventDraft } from '../components/EventSheet.tsx'
import { TaskSheet, type TaskDraft } from '../components/TaskSheet.tsx'
import { SharePanel } from '../components/SharePanel.tsx'
import { Icon } from '../components/Icon.tsx'
import { useAuth } from '../store/auth.ts'
import { onServerEvent } from '../realtime/socket.ts'
import {
  clockTime,
  dayKey,
  dayTitle,
  monthGrid,
  monthTitle,
  sameMonth,
  KIND_ICONS,
} from '../lib/planner.ts'

/**
 * ПЛАНЕР
 *
 * Сетка месяца сверху и список дней под ней. Сетка отвечает на вопрос
 * «когда у нас свободно», список — «что сегодня». По отдельности ни одна
 * из двух половин не работает: в сетке не читается, что именно за дело,
 * а в списке не видно месяца целиком.
 *
 * Пустые дни в списке пропускаются: листать две недели пустоты, чтобы
 * дойти до ближайшего занятия, — занятие бессмысленное.
 */

type Occurrence = {
  id: string
  occurrenceAt: string
  repeating: boolean
  title: string
  description: string | null
  location: string | null
  kind: string
  startsAt: string
  endsAt: string | null
  allDay: boolean
  color: string | null
  calendar: { id: string; name: string; mine: boolean }
  participants: { id: string; displayName: string; avatarUrl: string | null; status: string }[]
  myStatus: string | null
  person?: string
}

type Task = {
  id: string
  title: string
  notes: string | null
  dueAt: string | null
  allDay: boolean
  priority: number
  done: boolean
  assignee: { id: string; displayName: string; avatarUrl: string | null } | null
  calendarId: string | null
}

type Calendar = { id: string; name: string; color: string; isDefault: boolean; mine: boolean }

const WEEK = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']

export function PlannerPage() {
  const me = useAuth((s) => s.user)
  const people = useFamilyPeople(me?.id)

  const [month, setMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  const [calendars, setCalendars] = useState<Calendar[]>([])
  const [events, setEvents] = useState<Occurrence[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [eventDraft, setEventDraft] = useState<EventDraft | null>(null)
  const [taskDraft, setTaskDraft] = useState<TaskDraft | null>(null)
  const [sharing, setSharing] = useState<Calendar | null>(null)
  const [tab, setTab] = useState<'days' | 'tasks'>('days')

  const dayRefs = useRef(new Map<string, HTMLDivElement>())

  const load = () => {
    const from = new Date(month.getFullYear(), month.getMonth(), 1)
    // Захватываем хвосты соседних месяцев: они видны в сетке
    from.setDate(from.getDate() - 7)
    const to = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59)
    to.setDate(to.getDate() + 7)

    api<{ events: Occurrence[]; tasks: Task[] }>(
      `/api/planner/agenda?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
    )
      .then((data) => {
        setEvents(data.events)
        setTasks(data.tasks)
        setError(null)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [month])

  useEffect(() => {
    api<{ calendars: Calendar[] }>('/api/planner/calendars')
      .then((data) => setCalendars(data.calendars))
      .catch(() => setCalendars([]))
  }, [])

  // Событие могли изменить с карточки в переписке или с другого устройства
  useEffect(() => {
    return onServerEvent((event) => {
      if (event.type === 'card:changed' && (event.entityType === 'EVENT' || event.entityType === 'TASK')) {
        load()
      }
    })
  }, [month])

  /** Что в какой день: и события, и дела со сроком в одной куче. */
  const byDay = useMemo(() => {
    const map = new Map<string, { events: Occurrence[]; tasks: Task[] }>()

    const bucket = (key: string) => {
      let found = map.get(key)
      if (!found) {
        found = { events: [], tasks: [] }
        map.set(key, found)
      }
      return found
    }

    for (const event of events) bucket(dayKey(event.startsAt)).events.push(event)
    for (const task of tasks) if (task.dueAt) bucket(dayKey(task.dueAt)).tasks.push(task)

    return map
  }, [events, tasks])

  const grid = useMemo(() => monthGrid(month), [month])

  const days = useMemo(
    () =>
      [...byDay.entries()]
        .filter(([key]) => {
          const [y, m] = key.split('-').map(Number)
          return (y ?? 0) === month.getFullYear() && (m ?? 0) - 1 === month.getMonth()
        })
        .sort(([a], [b]) => a.localeCompare(b)),
    [byDay, month],
  )

  const today = dayKey(new Date())

  const goToday = () => {
    const now = new Date()
    setMonth(new Date(now.getFullYear(), now.getMonth(), 1))
    // Ждём отрисовки нового месяца, иначе прокручивать ещё нечего
    setTimeout(() => {
      dayRefs.current.get(dayKey(now))?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 50)
  }

  const newEvent = (day?: Date) => {
    const start = day ?? new Date()
    if (!day) start.setMinutes(0, 0, 0)
    else start.setHours(12, 0, 0, 0)

    setEventDraft({
      title: '',
      kind: 'EVENT',
      allDay: false,
      startsAt: start.toISOString(),
      endsAt: null,
    })
  }

  const rsvp = async (event: Occurrence, status: string) => {
    setEvents((current) =>
      current.map((e) => (e.id === event.id ? { ...e, myStatus: status } : e)),
    )
    await api(`/api/planner/events/${event.id}/rsvp`, { method: 'POST', body: { status } }).catch(
      load,
    )
  }

  const toggleTask = async (task: Task) => {
    setTasks((current) =>
      current.map((t) => (t.id === task.id ? { ...t, done: !t.done } : t)),
    )
    await api(`/api/planner/tasks/${task.id}`, {
      method: 'PATCH',
      body: { done: !task.done },
    }).catch(load)
  }

  const mine = calendars.find((c) => c.mine && c.isDefault) ?? calendars.find((c) => c.mine)

  return (
    <div className="section">
      <header className="section-head">
        <button
          className="icon-btn"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          aria-label="Предыдущий месяц"
        >
          ‹
        </button>

        <div className="section-title">
          <h1>{monthTitle(month)}</h1>
        </div>

        <button
          className="icon-btn"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          aria-label="Следующий месяц"
        >
          ›
        </button>

        <button className="link-btn" onClick={goToday}>
          Сегодня
        </button>

        {mine && (
          <button className="icon-btn" onClick={() => setSharing(mine)} title="Кому открыт">
            <Icon name="family" size={21} />
          </button>
        )}

        <button
          className="icon-btn"
          onClick={() => (tab === 'days' ? newEvent() : setTaskDraft({ title: '' }))}
          aria-label="Добавить"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      <div className="planner-tabs">
        <button
          className={'tab-btn' + (tab === 'days' ? ' is-active' : '')}
          onClick={() => setTab('days')}
        >
          Календарь
        </button>
        <button
          className={'tab-btn' + (tab === 'tasks' ? ' is-active' : '')}
          onClick={() => setTab('tasks')}
        >
          Дела{tasks.filter((t) => !t.done).length > 0 && ` · ${tasks.filter((t) => !t.done).length}`}
        </button>
      </div>

      {tab === 'days' ? (
        <>
          <div className="month-grid">
            {WEEK.map((name) => (
              <div key={name} className="month-weekday">
                {name}
              </div>
            ))}

            {grid.map((day) => {
              const key = dayKey(day)
              const inDay = byDay.get(key)
              const count = (inDay?.events.length ?? 0) + (inDay?.tasks.length ?? 0)

              return (
                <button
                  key={key}
                  className={
                    'month-day' +
                    (sameMonth(day, month) ? '' : ' is-other') +
                    (key === today ? ' is-today' : '')
                  }
                  onClick={() => {
                    if (count > 0) {
                      dayRefs.current.get(key)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                    } else {
                      newEvent(new Date(day))
                    }
                  }}
                >
                  <span className="month-day-number">{day.getDate()}</span>
                  {count > 0 && (
                    <span className="month-day-dots">
                      {inDay?.events.slice(0, 3).map((e) => (
                        <span
                          key={e.id + e.occurrenceAt}
                          className="month-dot"
                          style={{ background: e.color ?? 'var(--accent)' }}
                        />
                      ))}
                      {(inDay?.tasks.length ?? 0) > 0 && <span className="month-dot is-task" />}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="section-body scroll">
            {loading && (
              <div className="center-screen" style={{ height: 80 }}>
                <div className="spinner" />
              </div>
            )}

            {!loading && days.length === 0 && (
              <p className="empty-hint">
                В этом месяце пусто. Нажми на день в сетке, чтобы завести событие.
              </p>
            )}

            {days.map(([key, inDay]) => {
              const [y, m, d] = key.split('-').map(Number)
              const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)

              return (
                <div
                  key={key}
                  className={'day-block' + (key === today ? ' is-today' : '')}
                  ref={(node) => {
                    if (node) dayRefs.current.set(key, node)
                    else dayRefs.current.delete(key)
                  }}
                >
                  <div className="day-head">{dayTitle(date)}</div>

                  {inDay.events.map((event) => (
                    <EventRow
                      key={event.id + event.occurrenceAt}
                      event={event}
                      meId={me?.id}
                      onOpen={() =>
                        event.person
                          ? undefined
                          : setEventDraft({
                              id: event.id,
                              occurrenceAt: event.occurrenceAt,
                              repeating: event.repeating,
                              title: event.title,
                              description: event.description,
                              location: event.location,
                              kind: event.kind,
                              startsAt: event.startsAt,
                              endsAt: event.endsAt,
                              allDay: event.allDay,
                              calendarId: event.calendar.id,
                              participants: event.participants,
                            })
                      }
                      onRsvp={(status) => void rsvp(event, status)}
                    />
                  ))}

                  {inDay.tasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onToggle={() => void toggleTask(task)}
                      onOpen={() => setTaskDraft(task)}
                    />
                  ))}
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <TasksView onToggle={toggleTask} onOpen={setTaskDraft} />
      )}

      {error && <div className="sheet-error">{error}</div>}

      {eventDraft && (
        <EventSheet
          draft={eventDraft}
          calendars={calendars}
          people={people}
          onClose={() => setEventDraft(null)}
          onSaved={load}
        />
      )}

      {taskDraft && (
        <TaskSheet
          draft={taskDraft}
          people={people}
          onClose={() => setTaskDraft(null)}
          onSaved={load}
        />
      )}

      {sharing && (
        <SharePanel
          entityType="CALENDAR"
          entityId={sharing.id}
          onClose={() => setSharing(null)}
          title={`Кому открыт «${sharing.name}»`}
        />
      )}
    </div>
  )
}

function EventRow({
  event,
  meId,
  onOpen,
  onRsvp,
}: {
  event: Occurrence
  meId: string | undefined
  onOpen: () => void
  onRsvp: (status: string) => void
}) {
  const invited = event.participants.some((p) => p.id === meId)

  return (
    <div className="event-row">
      <span className="event-time">
        {event.allDay ? 'весь день' : clockTime(event.startsAt)}
        {!event.allDay && event.endsAt && (
          <span className="event-till">до {clockTime(event.endsAt)}</span>
        )}
      </span>

      <button className="event-main" onClick={onOpen}>
        <span className="event-bar" style={{ background: event.color ?? 'var(--accent)' }} />

        <span className="event-text">
          <span className="event-title">
            <span aria-hidden="true">{KIND_ICONS[event.kind] ?? '📌'}</span> {event.title}
          </span>

          <span className="event-sub">
            {event.location && <span>{event.location}</span>}
            {!event.calendar.mine && <span>{event.calendar.name}</span>}
            {event.description && <span>{event.description}</span>}
          </span>
        </span>

        {event.participants.length > 0 && (
          <span className="event-people">
            {event.participants.slice(0, 4).map((p) => (
              <span key={p.id} className={'event-person is-' + p.status} title={p.displayName}>
                <Avatar id={p.id} name={p.displayName} url={p.avatarUrl} size={22} />
              </span>
            ))}
          </span>
        )}
      </button>

      {/* Ответить «иду» можно только там, где тебя действительно зовут */}
      {invited && (
        <span className="event-rsvp">
          <button
            className={'rsvp-btn' + (event.myStatus === 'yes' ? ' is-yes' : '')}
            onClick={() => onRsvp('yes')}
            title="Иду"
          >
            ✓
          </button>
          <button
            className={'rsvp-btn' + (event.myStatus === 'no' ? ' is-no' : '')}
            onClick={() => onRsvp('no')}
            title="Не иду"
          >
            ✕
          </button>
        </span>
      )}
    </div>
  )
}

function TaskRow({
  task,
  onToggle,
  onOpen,
}: {
  task: Task
  onToggle: () => void
  onOpen: () => void
}) {
  return (
    <div className={'item-row' + (task.done ? ' is-done' : '')}>
      <button className="item-check" onClick={onToggle} aria-label={task.done ? 'Вернуть' : 'Сделано'}>
        {task.done ? '✓' : ''}
      </button>

      <button className="item-main" onClick={onOpen}>
        <span className="item-title">
          {task.priority > 0 && <span className="task-flag">!</span>}
          {task.title}
        </span>

        <span className="item-marks">
          {task.dueAt && !task.allDay && <span>{clockTime(task.dueAt)}</span>}
          {task.assignee && (
            <Avatar
              id={task.assignee.id}
              name={task.assignee.displayName}
              url={task.assignee.avatarUrl}
              size={20}
            />
          )}
        </span>
      </button>
    </div>
  )
}

/** Все дела, а не только те, что попали в этот месяц. */
function TasksView({
  onToggle,
  onOpen,
}: {
  onToggle: (task: Task) => void
  onOpen: (task: Task) => void
}) {
  const [tasks, setTasks] = useState<Task[] | null>(null)
  const [showDone, setShowDone] = useState(false)

  const load = () => {
    api<{ tasks: Task[] }>(`/api/planner/tasks?done=${showDone ? 'all' : 'no'}`)
      .then((data) => setTasks(data.tasks))
      .catch(() => setTasks([]))
  }

  useEffect(load, [showDone])

  return (
    <div className="section-body scroll">
      {tasks === null && (
        <div className="center-screen" style={{ height: 80 }}>
          <div className="spinner" />
        </div>
      )}

      {tasks?.length === 0 && <p className="empty-hint">Дел нет. Так и должно быть.</p>}

      {tasks?.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          onToggle={() => {
            onToggle(task)
            setTasks((current) =>
              (current ?? []).map((t) => (t.id === task.id ? { ...t, done: !t.done } : t)),
            )
          }}
          onOpen={() => onOpen(task)}
        />
      ))}

      <button className="link-btn" onClick={() => setShowDone((v) => !v)}>
        {showDone ? 'Скрыть сделанное' : 'Показать сделанное'}
      </button>
    </div>
  )
}
